
import React, { useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { Loader2 } from 'lucide-react';
import CommunityError from '@/pages/CommunityError';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

/**
 * Enhanced route protection component that handles both authentication and permission checking
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [] 
}) => {
  const { user, loading: authLoading } = useAuth();
  const { isCheckingAccess, isAllowedTo } = useCommunityMember();
  const location = useLocation();
  const [permissionState, setPermissionState] = React.useState({
    isChecking: true,
    hasPermission: false
  });

  // Create stable checkPermissions function with useCallback
  const checkPermissions = useCallback(async () => {
    // If no permissions required, grant access
    if (requiredPermissions.length === 0) {
      setPermissionState({
        isChecking: false,
        hasPermission: true
      });
      return;
    }

    // If still checking access or no user, wait
    if (isCheckingAccess || !user) {
      setPermissionState({
        isChecking: true,
        hasPermission: false
      });
      return;
    }

    try {
      // Check if user has all required permissions
      const permissionChecks = await Promise.all(
        requiredPermissions.map(permission => isAllowedTo(permission))
      );
      
      const hasAllPermissions = permissionChecks.every(result => result === true);
      setPermissionState({
        isChecking: false,
        hasPermission: hasAllPermissions
      });
    } catch (error) {
      console.error('Error checking permissions:', error);
      setPermissionState({
        isChecking: false,
        hasPermission: false
      });
    }
  }, [user?.id, requiredPermissions.join(','), isCheckingAccess, isAllowedTo]);

  // Check permissions when dependencies change
  React.useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  // Show loading state while checking authentication or access
  if (authLoading || isCheckingAccess || (requiredPermissions.length > 0 && permissionState.isChecking)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to auth page if user is not authenticated
  if (!user) {
    const fullPath = location.pathname + location.search + location.hash;
    console.log("Redirecting to auth, preserving full path:", fullPath);
    
    return (
      <Navigate 
        to={`/auth?returnTo=${encodeURIComponent(fullPath)}`} 
        replace 
      />
    );
  }

  // Show access denied if user lacks required permissions
  if (requiredPermissions.length > 0 && !permissionState.hasPermission) {
    return (
      <CommunityError 
        error={{
          title: "Access Denied",
          message: `You don't have permission to access this page. Required permissions: ${requiredPermissions.join(', ')}`
        }}
      />
    );
  }

  // If user is authenticated and has permissions, render the protected route
  return <>{children}</>;
};

export default ProtectedRoute;
